'use client'

// Chart hand-rolled (tanpa library). Burndown = SVG line; velocity & heatmap = CSS.
import type { BurndownPoint, VelocityPoint } from '@/lib/dev-sprint'

export function Burndown({ series, todayISO }: { series: BurndownPoint[]; todayISO: string }) {
  const W = 320, H = 130, PADL = 26, PADB = 18, PADT = 8, PADR = 6
  if (series.length < 2) return <Empty>Belum ada data burndown</Empty>
  const max = Math.max(1, ...series.map((p) => p.ideal))
  const iw = W - PADL - PADR
  const ih = H - PADT - PADB
  const x = (i: number) => PADL + (i / (series.length - 1)) * iw
  const y = (v: number) => PADT + (1 - v / max) * ih
  const line = (get: (p: BurndownPoint) => number | null) => {
    const pts = series.map((p, i) => ({ i, v: get(p) })).filter((o) => o.v != null)
    return pts.map((o, k) => `${k ? 'L' : 'M'}${x(o.i).toFixed(1)},${y(o.v as number).toFixed(1)}`).join(' ')
  }
  const todayIdx = series.findIndex((p) => p.day === todayISO)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 180 }}>
      {/* gridlines */}
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={PADL} y1={PADT + f * ih} x2={W - PADR} y2={PADT + f * ih} stroke="#EFEDE7" strokeWidth={1} />
          <text x={2} y={PADT + f * ih + 3} fontSize={8} fill="#AEADA8">{Math.round(max * (1 - f))}</text>
        </g>
      ))}
      {todayIdx >= 0 && <line x1={x(todayIdx)} y1={PADT} x2={x(todayIdx)} y2={PADT + ih} stroke="#D4A843" strokeWidth={1} strokeDasharray="2 2" />}
      <path d={line((p) => p.ideal)} fill="none" stroke="#C9C7C0" strokeWidth={1.5} strokeDasharray="4 3" />
      <path d={line((p) => p.actual)} fill="none" stroke="#1A1916" strokeWidth={2} />
      <text x={PADL} y={H - 4} fontSize={8} fill="#AEADA8">{series[0].day.slice(5)}</text>
      <text x={W - PADR} y={H - 4} fontSize={8} fill="#AEADA8" textAnchor="end">{series[series.length - 1].day.slice(5)}</text>
    </svg>
  )
}

export function VelocityBars({ data }: { data: VelocityPoint[] }) {
  if (data.length === 0) return <Empty>Belum ada sprint selesai</Empty>
  const max = Math.max(1, ...data.map((d) => d.points))
  return (
    <div className="flex items-end gap-2 h-[150px] pt-2">
      {data.map((d) => (
        <div key={d.sprintId} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5 min-w-0">
          <span className="text-[11px] font-bold text-[#4A4844] tabular-nums">{d.points}</span>
          <div
            className="w-full rounded-md"
            style={{ height: `${Math.max(6, (d.points / max) * 100)}%`, backgroundColor: d.current ? '#D4A843' : '#E0DED7' }}
            title={`${d.name}: ${d.points} pts`}
          />
          <span className="text-[10px] text-[#AEADA8] truncate max-w-full">{d.name}</span>
        </div>
      ))}
    </div>
  )
}

export function Heatmap({
  rows, weeks, capacity,
}: {
  rows: { name: string; load: number[] }[]
  weeks: number
  capacity: number
}) {
  const cellColor = (v: number) => {
    if (v === 0) return { bg: '#F5F4F0', fg: '#C9C7C0' }
    const ratio = v / capacity
    if (ratio > 1) return { bg: '#F6C6C0', fg: '#8A2A22' }
    if (ratio > 0.75) return { bg: '#FBE2B8', fg: '#8A5B00' }
    if (ratio > 0.4) return { bg: '#DCEFD9', fg: '#2C6E36' }
    return { bg: '#EAF3E9', fg: '#4A7A4E' }
  }
  return (
    <div className="overflow-x-auto">
      <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${weeks}, minmax(48px, 1fr))` }} className="gap-1 min-w-max">
        <div />
        {Array.from({ length: weeks }, (_, i) => (
          <div key={i} className="text-[10px] font-semibold text-[#8B8A85] text-center pb-1">W{i + 1}</div>
        ))}
        {rows.map((r) => (
          <div key={r.name} className="contents">
            <div className="text-[12px] font-medium text-[#4A4844] truncate flex items-center pr-2">{r.name}</div>
            {r.load.map((v, i) => {
              const c = cellColor(v)
              return (
                <div key={i} className="h-9 rounded-md flex items-center justify-center text-[12px] font-bold tabular-nums" style={{ backgroundColor: c.bg, color: c.fg }} title={`${v} pts`}>
                  {v || ''}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#AEADA8] mt-2">Merah = melebihi kapasitas {capacity} pts/minggu.</p>
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="h-[130px] flex items-center justify-center text-[12px] text-[#AEADA8]">{children}</div>
}
